import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Subscribe to WhatsApp real-time events via Supabase Realtime
 */
export function useWhatsAppRealtime() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      return;
    }

    console.log('[Realtime] Setting up subscriptions for userId:', userId);

    const channel = supabase
      .channel(`whatsapp-realtime-${userId}`)

      // New messages
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'whatsapp_messages',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        // Invalidate messages query for the affected chat
        queryClient.invalidateQueries({
          queryKey: ['whatsapp', 'messages', payload.new.jid],
        });

        // Update chat list (new message affects last_message_text)
        queryClient.invalidateQueries({
          queryKey: ['whatsapp', 'chats'],
        });
      })

      // Chat updates (unread count, last message)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'whatsapp_chats',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        queryClient.invalidateQueries({
          queryKey: ['whatsapp', 'chats'],
        });
      })

      // Connection status updates
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'whatsapp_sessions',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        console.log('[Realtime] whatsapp_sessions UPDATE received:', payload);
        const session = payload.new as any;

        // Check if QR code is expired
        const qrExpired = session.qr_expires_at
          ? new Date(session.qr_expires_at) < new Date()
          : true;

        // Transform to match API response structure
        const statusData = {
          connected: session.connected,
          status: session.connected ? 'connected' : qrExpired ? 'disconnected' : 'qr',
          instanceName: session.instance_name,
          phoneNumber: session.phone_number || undefined,
          qrCode: !session.connected && !qrExpired ? session.qr_code : undefined,
          qrExpiresAt: session.qr_expires_at || undefined,
        };

        console.log('[Realtime] Updating status cache:', statusData);
        queryClient.setQueryData(
          ['whatsapp', 'status'],
          statusData
        );

        // Also invalidate to trigger refetch (ensures components update)
        queryClient.invalidateQueries({ queryKey: ['whatsapp', 'status'] });
      })

      .subscribe((status) => {
        console.log('[Realtime] Subscription status:', status);
      });

    return () => {
      console.log('[Realtime] Unsubscribing from whatsapp-realtime channel');
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}
